import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $transferRequestsApprovedListStore, $transferRequestsGridListStore, $transferUpdateStatusStore } from '@lib/store/transferRequests';
import { $transferRequestsListStore } from '@lib/store/transferRequests/transferRequestsListStore';
import { RoutePaths } from '@shared/utils';
import type { TransferRequestResponse } from '@shared/api/rest/transferRequests';
import { TransferRequestStatusEnum } from '@shared/api/rest/transferRequests/models/enums';
import { PageContainer } from '@shared/widgets';
import { Navigation } from './widgets';
import { UpcomingPayments } from './subPages/upcomingPayments';
import { ApprovePayments } from './subPages/approvePayments';
import { List as Recipients } from './subPages/widgets/list';
import { TransferRequests } from './subPages';
import { TransferRequestConfirmModal } from './subPages/widgets/modal/transferRequestConfirmationModal';
import { OTPModal } from './subPages/widgets/modal/otpModal';

export enum TransferModalType {
  APPROVE = 'Approve',
  REJECT = 'Reject',
}

export const Payments = () => {
  const updateData = useUnit($transferUpdateStatusStore.store);

  const [readOnlyModalData, setReadOnlyModalData] = useState<TransferRequestResponse | null>(null);
  const [approvalModalData, setApprovalModalData] = useState<TransferRequestResponse | null>(null);
  const [otpModal, setOTPModal] = useState<TransferRequestResponse | null>(null);
  const [transfer, setTransfer] = useState<TransferModalType | undefined>();

  useEffect(() => {
    if (transfer) {
      const timer = setTimeout(() => {
        setTransfer(undefined);
      }, 5000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [transfer]);

  const onPaymentSelect = (item: TransferRequestResponse) => {
    if (item.status === TransferRequestStatusEnum.WaitingForApproval) {
      setApprovalModalData(item);
    } else {
      setReadOnlyModalData(item);
    }
  };

  const closeModal = () => {
    setReadOnlyModalData(null);
    setApprovalModalData(null);
  };

  const openOTPModal = (data: TransferRequestResponse) => {
    setOTPModal(data);
    setReadOnlyModalData(null);
    setApprovalModalData(null);
  };

  const closeOTPModal = async () => {
    setOTPModal(null);
    await $transferRequestsGridListStore.events.update({
      Count: 10,
      FromIndex: 0,
    });
    await $transferRequestsListStore.events.update({
      status: TransferRequestStatusEnum.Submitted,
      Count: 5,
      FromIndex: 0,
    });
    await $transferRequestsApprovedListStore.events.update({
      status: TransferRequestStatusEnum.WaitingForApproval,
      Count: 5,
      FromIndex: 0,
    });
  };

  return (
    <PageContainer title='Payments' header='Payments'>
      <div className='flex flex-col gap-6'>
        {transfer && (
          <div
            className={clsx(
              'bg-white border w-dvw fixed top-0 left-0 right-0 z-[5000]',
              transfer === TransferModalType.APPROVE ? 'border-green-500' : 'border-gray-500',
            )}
          >
            <p className={clsx('font-semibold text-center p-3', transfer === TransferModalType.APPROVE ? 'text-green-500' : 'text-gray-500')}>
              {transfer === TransferModalType.APPROVE ? (
                <Link to={RoutePaths.account.onNavigate(updateData.data?.fromAccount.id as number)}>Transfer request was approved</Link>
              ) : (
                'Transfer Rejected'
              )}
            </p>
          </div>
        )}
        <Navigation />
        <div className='grid grid-cols-2 gap-4'>
          <UpcomingPayments onSelect={onPaymentSelect} />
          <ApprovePayments onSelect={onPaymentSelect} />
        </div>
        <Recipients />
        <TransferRequests />
      </div>
      {(readOnlyModalData || approvalModalData) && (
        <TransferRequestConfirmModal
          transferData={readOnlyModalData || approvalModalData!}
          onClose={closeModal}
          readOnly={!!readOnlyModalData}
          transactionId={readOnlyModalData?.id || approvalModalData!.id}
          noDocumentUpload
          setOTPModal={openOTPModal}
          closeOTPModal={closeOTPModal}
          isApproval={!!approvalModalData}
          setTransfer={setTransfer}
          isOpen={readOnlyModalData || approvalModalData ? true : false}
        />
      )}
      {otpModal && <OTPModal onClose={closeOTPModal} data={otpModal} setTransfer={setTransfer} />}
    </PageContainer>
  );
};
